import { Injectable, signal } from "@angular/core";
import { Observable, Subject, firstValueFrom, interval, of, timer } from "rxjs";
import { catchError, filter, finalize, map, switchMap, take, takeUntil } from "rxjs/operators";
import { Firestore, doc, docData } from "@angular/fire/firestore";
import { Router } from "@angular/router";
import { v4 as uuidv4 } from "uuid";
import { Data } from "@angular/router";
import { PaymentService } from "src/app/services/payment.service";
import { AuthService } from "@core/auth.service";
import { CashPurchaseService } from "src/app/services/cash-purchase.service";
import { SyndicateRepositoryService } from "../../syndicate/syndicate-repository.service";
import { CheckoutVoucherService } from "./checkout-voucher.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { CheckoutTrackingHelper } from "./checkout-tracking.helper";

/**
 * Payment context interface for passing payment-related data
 */
export interface PaymentContext {
  purchaseDetail: Data;
  cardDetailId?: Data;
  userId: string;
  preferredCurrency: string;
  dialogData?: Data;
  queryParams?: Data;
  scrollDepthProvider?: () => number;
  selectedCard?: string;
  selectedCardForAddCardCase?: string;
  prosub_id?: string;
  tp1?: string;
  tp2?: string;
  tp3?: string;
  t_id?: string;
  upsell_ID?: string;
  paymentOfferID?: string;
  other?: string;
  journeyId?: number;
  cardArrlength?: number;
  selectedDiscountValue?: number;
  selectedDiscountVoucherData?: Data | null;
  updatedPrizeValue?: number;
  isCheckedValue?: string;
  addonAmount?: Data[];
  totalAmount?: number;
  customOfferAwardId?: number;
  preferredCurrencySignal?: () => string;
  userData?: Data;
  checkoutComPtype?: number;
  statusFlag?: boolean;
  successToast?: string;
  failureToast?: string;
}

/**
 * Payment result interface
 */
export interface PaymentResult {
  success: boolean;
  customerPurchaseId?: string;
  encryptedCustomerPurchaseId?: string;
  response?: Data;
  error?: string;
  errorCode?: string;
}

/**
 * Payment callbacks interface
 */
export interface PaymentCallbacks {
  onSuccess?: (result: PaymentResult) => void;
  onError?: (error: any, errorCode?: string) => void;
  onRejection?: (errorCode: string) => void;
  onFinalize?: () => void;
  onNavigate?: (path: string, state?: any, queryParams?: any) => Promise<boolean | void>;
  onDismissModal?: () => Promise<boolean | void>;
}

@Injectable()
export class StorePaymentProcessorService {
  // State properties
  existingCardPaymentStatus = false;
  responseObject: Data;
  displayResponse: Data;
  addNewCardInProgress = false;
  selectedCard = "10";
  selectedCardForAddCardCase = "10";
  selectedCard$ = new Subject<string>();
  cardOfferpurchaseDetail: Data = {} as Data;
  cardOfferDetails: Data[] = [];
  processingStates: boolean[] = [];
  activeCardIndex: number[] = [];
  currentOfferCard: string;
  updatedSuccessToast: string;
  updatedFailureToast: string;
  isSandbox = signal<boolean>(false);

  private statusFlag = false;

  constructor(
    private paymentService: PaymentService,
    private authService: AuthService,
    private firestore: Firestore,
    private cashPurchaseService: CashPurchaseService,
    private syndicateService: SyndicateRepositoryService,
    private checkoutVoucherService: CheckoutVoucherService,
    private voucherService: VoucherService,
    private mixPanelService: MixpanelService,
    private router: Router,
    private checkoutTrackingHelper: CheckoutTrackingHelper
  ) {}

  /**
   * Get the currently checked card detail ID
   */
  getCheckCardDetailId(storedCards: any[], selectedCardData: string): Data | undefined {
    if (!storedCards || !selectedCardData) return undefined;
    return storedCards.find((x) => x?.id === selectedCardData);
  }

  /**
   * Process payment for regular store purchases
   */
  proceedToPay(context: PaymentContext, callbacks?: PaymentCallbacks): Observable<PaymentResult> {
    return new Observable((observer) => {
      // Prepare purchase detail with tracking parameters
      const purchaseDetail = this.preparePurchaseDetail(context);

      // Handle top-up case
      if (purchaseDetail.isTopUp) {
        this.processTopUpPayment(context, callbacks).subscribe({
          next: (result) => observer.next(result),
          error: (err) => observer.error(err),
        });
        return;
      }

      this.existingCardPaymentStatus = true;

      const cardDetailId = context.cardDetailId || this.getCheckCardDetailId(
        context.purchaseDetail?.storedCards || [],
        context.purchaseDetail?.selectedCardData
      );

      this.paymentService
        .insertCustomerPurchase(
          { ...purchaseDetail, ...cardDetailId },
          "/api_post_request/insert_customer_purchase_store_payment"
        )
        .subscribe({
          next: (result) => {
            if (result?.status === "rejected") {
              this.checkoutVoucherService.resetAll();
              this.handlePaymentRejection(result?.error_code, callbacks);
              observer.error({ error: "Payment rejected", errorCode: result?.error_code });
              return;
            }

            const customerPurchaseId = result?.response?.customer_purchase_id;
            const encryptedCustomerPurchaseId = result?.response?.encrypted_customer_purchase_id;
            this.paymentService.cashPurchaseCustomerPurchaseId$.next(encryptedCustomerPurchaseId);

            if (result && result?.response && result?.response?.status === "SUCCESS") {
              this.processExistingCardFlow(
                customerPurchaseId,
                cardDetailId,
                purchaseDetail,
                context,
                callbacks
              ).subscribe({
                next: (processResult) => observer.next(processResult),
                error: (err) => observer.error(err),
              });
            } else {
              this.checkoutVoucherService.resetAll();
              this.handlePaymentRejection("1051", callbacks);
              observer.error({ error: "Failed to make payment request", errorCode: "1051" });
            }
          },
          error: (err) => {
            this.checkoutVoucherService.resetAll();
            this.handlePaymentRejection("1050", callbacks);
            observer.error(err);
          },
        });
    });
  }

  /**
   * Process payment for private syndicate creation
   */
  proceedToPayForPS(context: PaymentContext, callbacks?: PaymentCallbacks): Observable<PaymentResult> {
    return new Observable((observer) => {
      this.syndicateService.saveSyndicatPurchaseDetails(context.purchaseDetail?.psId, context.purchaseDetail);

      this.paymentService.processPSCreatingCart(context.purchaseDetail).subscribe({
        next: (result) => {
          if (result?.status === "rejected") {
            this.handlePaymentRejection(result?.error_code, callbacks);
            observer.error({ error: "Payment rejected", errorCode: result?.error_code });
            return;
          }

          if (result && result?.status === "SUCCESS") {
            const cardDetailId = context.cardDetailId || this.getCheckCardDetailId(
              context.purchaseDetail?.storedCards || [],
              context.purchaseDetail?.selectedCardData
            );

            this.paymentService
              .processCardPayment(
                result?.payload?.order_id,
                cardDetailId,
                "/api_post_request/process_store_payment_existing_card"
              )
              .pipe(
                finalize(() => {
                  callbacks?.onFinalize?.();
                })
              )
              .subscribe({
                next: (processPayResult) => {
                  if (!processPayResult) {
                    observer.next({ success: false });
                    return;
                  }

                  if (processPayResult?.status === "rejected") {
                    this.handlePaymentRejection(processPayResult?.error_code, callbacks);
                    observer.error({ error: "Payment rejected", errorCode: processPayResult?.error_code });
                    return;
                  }

                  if (
                    processPayResult &&
                    processPayResult?.response &&
                    processPayResult?.response?.status === "SUCCESS"
                  ) {
                    // Update Firestore for private syndicate
                    this.authService
                      .getV2CloudFunction("updatefirestoredoc", {
                        path_id: "rDShdjSvpksqa3To0u0R",
                        doc_data: { initial_payment: true },
                        dv: { docId: context.purchaseDetail.psId },
                        method: "update",
                      })
                      .subscribe({
                        error: (err) => {
                          this.authService.errorHandlerLogs("Error in updatefirestoredoc cloud function", err);
                        },
                      });

                    context.purchaseDetail.status = "completed";
                    this.syndicateService.saveSyndicatPurchaseDetails(
                      context.purchaseDetail?.syndicate_id,
                      context.purchaseDetail
                    );

                    this.responseObject = { ...context.purchaseDetail, ...processPayResult?.response };
                    this.paymentService.updateSyndicateRequest(
                      this.responseObject,
                      processPayResult?.response?.syndicate_id
                    );

                    const paymentResult: PaymentResult = {
                      success: true,
                      customerPurchaseId: result?.payload?.order_id,
                      response: processPayResult?.response,
                    };

                    this.paymentService.emitSyndicatePurchaseComplete();
                    callbacks?.onSuccess?.(paymentResult);
                    observer.next(paymentResult);
                  } else {
                    this.handlePaymentRejection("1050", callbacks);
                    observer.error({ error: "Failed in processing payment", errorCode: "1050" });
                  }
                },
                error: (err) => {
                  this.handlePaymentRejection("1050", callbacks);
                  observer.error(err);
                },
              });
          } else {
            this.handlePaymentRejection("1051", callbacks);
            observer.error({ error: "Failed in make payment request", errorCode: "1051" });
          }
        },
        error: (err) => {
          this.handlePaymentRejection("1051", callbacks);
          observer.error(err);
        },
      });
    });
  }

  /**
   * Process payment for card offer promotions
   */
  cardofferProceedToPay(
    index: number,
    context: PaymentContext,
    cardOfferDetail: Data,
    callbacks?: PaymentCallbacks
  ): Observable<PaymentResult> {
    return new Observable((observer) => {
      // Prepare card offer purchase detail
      const cardOfferPurchaseDetail = this.prepareCardOfferPurchaseDetail(context, cardOfferDetail);

      const cardDetailId = context.cardDetailId || this.getCheckCardDetailId(
        context.purchaseDetail?.storedCards || [],
        context.purchaseDetail?.selectedCardData
      );

      this.paymentService
        .insertCustomerPurchase(
          { ...cardOfferPurchaseDetail, ...cardDetailId },
          "/api_post_request/insert_customer_purchase_store_payment"
        )
        .subscribe({
          next: (result: Data) => {
            if (result?.status === "rejected") {
              this.handlePaymentRejection(result?.error_code, callbacks);
              observer.error({ error: "Payment rejected", errorCode: result?.error_code });
              return;
            }

            const customerPurchaseId = result?.response?.customer_purchase_id;
            const encryptedCustomerPurchaseId = result?.response?.encrypted_customer_purchase_id;
            this.paymentService.cashPurchaseCustomerPurchaseId$.next(encryptedCustomerPurchaseId);

            if (result && result?.response && result?.response?.status === "SUCCESS") {
              this.paymentService
                .processCardPayment(
                  result?.response?.customer_purchase_id,
                  cardDetailId,
                  "/api_post_request/process_store_payment_existing_card"
                )
                .pipe(
                  finalize(() => {
                    callbacks?.onFinalize?.();
                  })
                )
                .subscribe({
                  next: (processPayResult: Data) => {
                    if (!processPayResult) {
                      observer.next({ success: false });
                      return;
                    }

                    if (processPayResult?.status === "rejected") {
                      this.handlePaymentRejection("", callbacks, context.failureToast);
                      observer.error({ error: "Payment rejected" });
                      return;
                    }

                    if (
                      processPayResult &&
                      processPayResult?.response &&
                      processPayResult?.response?.status === "SUCCESS"
                    ) {
                      this.activeCardIndex.push(index);
                      this.responseObject = { ...context.purchaseDetail, ...processPayResult?.response, p_type: "10" };

                      // Show success toast for card offers
                      if (context.successToast) {
                        this.authService
                          .getConstantsMsg("")
                          .then((translatedMsg) => {
                            const defaultMsg = {
                              message: {
                                line_1: context.successToast,
                                line_2: "",
                              },
                              style: {
                                icon: "tick circle success.png",
                                type: "Success",
                              },
                            };
                            this.authService.showToastMessage(translatedMsg, defaultMsg);
                          })
                          .catch((error) => {
                            this.authService.errorHandlerLogs("Error fetching constants message", error);
                          });
                      }

                      const paymentResult: PaymentResult = {
                        success: true,
                        customerPurchaseId,
                        encryptedCustomerPurchaseId,
                        response: processPayResult?.response,
                      };

                      // Update Firestore payment document
                      this.updateFirestorePaymentDoc(
                        customerPurchaseId,
                        { ...context.purchaseDetail, ...processPayResult?.response, p_type: "10" },
                        context
                      );

                      // Update payment reference
                      const paymentDocId = String(uuidv4());
                      this.paymentService.updatePaymentReference(customerPurchaseId, paymentDocId).subscribe({
                        next: (res) => {
                          this.displayResponse = res;
                        },
                        error: (err) => {
                          this.authService.errorHandlerLogs("updatePaymentReference failed", err);
                        },
                      });

                      if (processPayResult.response?.syndicate_id) {
                        this.paymentService.updateSyndicateRequest(
                          { ...context.purchaseDetail, ...processPayResult?.response, p_type: "10" },
                          processPayResult.response?.syndicate_id
                        );
                      }

                      this.paymentService.emitSyndicatePurchaseComplete();
                      callbacks?.onSuccess?.(paymentResult);
                      observer.next(paymentResult);
                    } else {
                      this.handlePaymentRejection("1050", callbacks);
                      observer.error({ error: "Failed in processing payment", errorCode: "1050" });
                    }
                  },
                  error: (err) => {
                    this.handlePaymentRejection("1050", callbacks);
                    observer.error(err);
                  },
                });
            } else {
              this.handlePaymentRejection("1051", callbacks);
              observer.error({ error: "Failed in make payment request", errorCode: "1051" });
            }
          },
          error: (err) => {
            this.handlePaymentRejection("1051", callbacks);
            observer.error(err);
          },
        });
    });
  }

  /**
   * Process credit top-up payment
   */
  payCreditTopUp(context: PaymentContext, callbacks?: PaymentCallbacks): Observable<PaymentResult> {
    return new Observable((observer) => {
      const cardDetailId = context.cardDetailId || this.getCheckCardDetailId(
        context.purchaseDetail?.storedCards || [],
        context.purchaseDetail?.selectedCardData
      );
      context.purchaseDetail.isSandbox = this.isSandbox();
      this.paymentService
        .payForTopUp({ ...context.purchaseDetail, ...{ card: cardDetailId } })
        .pipe(
          finalize(() => {
            callbacks?.onFinalize?.();
          })
        )
        .subscribe({
          next: (res) => {
            if (res?.status === "rejected") {
              this.handlePaymentRejection(res?.error_code, callbacks);
              observer.error({ error: "Payment rejected", errorCode: res?.error_code });
              return;
            }

            if (res?.status === "SUCCESS") {
              const paymentResult: PaymentResult = {
                success: true,
                response: res,
              };
              this.paymentService.emitSyndicatePurchaseComplete();
              callbacks?.onSuccess?.(paymentResult);
              observer.next(paymentResult);
            } else {
              this.handlePaymentRejection("1103", callbacks);
              observer.error({ error: "Something went wrong. Please contact support.", errorCode: "1103" });
            }
          },
          error: (err) => {
            callbacks?.onError?.(err);
            observer.error(err);
          },
        });
    });
  }

  /**
   * Add new card flow
   */
  async addNewCard(context: PaymentContext, callbacks?: PaymentCallbacks): Promise<void> {
    localStorage.setItem("datatoSyndi", String(context.journeyId ?? ""));
    setTimeout(() => {
      const scrollDepth = Number(context.scrollDepthProvider?.() ?? 0).toFixed(2);
      localStorage.setItem("scrollDepthValue", scrollDepth);
    });

    if (context.updatedPrizeValue && context.isCheckedValue) {
      if (!context.purchaseDetail) {
        context.purchaseDetail = {};
      }
      context.purchaseDetail["upsell_amt"] = context.updatedPrizeValue;
      context.purchaseDetail["selected_upsell"] = context.isCheckedValue;
      context.purchaseDetail["add_on"] = context.addonAmount ?? [];
    }

    const addCardEventBody = this.checkoutTrackingHelper.buildCheckoutEventBody(context.dialogData ?? {}, {
      journeyId: context.journeyId,
      preExistingCard: false,
      cardCount: 0,
      offerId: context.dialogData?.offers?.offerid ?? "",
      cta: "Pay now",
      pType: context.selectedCard,
    });
    this.mixPanelService.GTMPopupInitializationForInboxNew("Add Card iframe Viewed", addCardEventBody);

    context.purchaseDetail["p_type"] = context.selectedCardForAddCardCase;
    if (context.purchaseDetail?.isCreatingPS) {
      context.purchaseDetail.cart[0]["p_type"] = context.selectedCardForAddCardCase;
    }
    this.paymentService.selected_p_type = context.selectedCardForAddCardCase;
    this.addNewCardInProgress = true;

    try {
      if (
        context.purchaseDetail?.["p_type"] !== context.checkoutComPtype &&
        ["10", "15"].includes(String(context.selectedCardForAddCardCase))
      ) {
        await this.processAddNewCardFlow(context, callbacks);
      } else if (
        String(context.selectedCardForAddCardCase) === "21" ||
        context.purchaseDetail?.p_type === "21"
      ) {
        await this.processCheckoutComFlow(context, callbacks);
      }
    } catch (error) {
      this.addNewCardInProgress = false;
      callbacks?.onError?.(error);
    }
  }

  /**
   * Poll payment status from Firestore
   */
  pollPaymentStatus(
    encryptedCustomerPurchaseId: string,
    purchaseDetail: Data,
    userId: string,
    callbacks?: PaymentCallbacks
  ): Observable<{ success: boolean; data?: Data }> {
    return new Observable((observer) => {
      let successHandled = false;
      const timeout$ = timer(20000);
      const pollInterval = 2000;

      const docRef = doc(this.firestore, `users/${userId}/customer_purchase/${encryptedCustomerPurchaseId}`);

      interval(pollInterval)
        .pipe(
          switchMap(() => {
            return docData(docRef).pipe(
              catchError(() => {
                return of(null);
              })
            );
          }),
          map((res) => {
            const desc = res?.payment_response_description;

            if (res?.processed_status === 2 || this.cashPurchaseService.isPaymentApproved(desc)) {
               this.paymentService.emitSyndicatePurchaseComplete();
              return { success: true, data: res };
            } else if (res?.processed_status == -1 || (desc && !this.cashPurchaseService.isPaymentApproved(desc))) {
              if (!this.statusFlag) {
                this.statusFlag = true;
                return null;
              }
              return { success: false, data: res };
            }
            return null;
          }),
          filter((res) => res != null),
          take(1),
          takeUntil(timeout$),
          catchError(() => {
            return of(null);
          })
        )
        .subscribe({
          next: (res) => {
            if (res?.success) {
              successHandled = true;
              const encrypt_syndicate_id = res?.data?.api_response?.response?.encrypt_syndicate_id;
              this.paymentService.emitSyndicatePurchaseComplete();
              callbacks?.onSuccess?.({ success: true, response: res.data });
              observer.next(res);
            }
          },
          complete: () => {
            if (!successHandled) {
              callbacks?.onError?.({ error: "Payment polling timeout" });
              observer.next({ success: false });
            }
          },
        });
    });
  }

  // ========== Private Helper Methods ==========

  /**
   * Process existing card payment flow
   */
  private processExistingCardFlow(
    customerPurchaseId: string,
    cardDetailId: Data,
    purchaseDetail: Data,
    context: PaymentContext,
    callbacks?: PaymentCallbacks
  ): Observable<PaymentResult> {
    return new Observable((observer) => {
      this.paymentService
        .processCardPayment(
          customerPurchaseId,
          cardDetailId,
          "/api_post_request/process_store_payment_existing_card"
        )
        .pipe(
          finalize(() => {
            callbacks?.onFinalize?.();
          })
        )
        .subscribe({
          next: (processPayResult) => {
            if (!processPayResult) {
              observer.next({ success: false });
              return;
            }

            if (processPayResult?.status === "rejected") {
              this.checkoutVoucherService.resetAll();
              this.handlePaymentRejection(processPayResult?.error_code, callbacks);
              observer.error({ error: "Payment rejected", errorCode: processPayResult?.error_code });
              return;
            }

            if (
              processPayResult &&
              processPayResult?.response &&
              processPayResult?.response?.status === "SUCCESS"
            ) {
              // Handle voucher tracking
              if (context.selectedDiscountValue && context.selectedDiscountValue > 0) {
                const event = context.selectedDiscountVoucherData?.autoAppliedVoucher
                  ? "voucher_auto_applied"
                  : "voucher_manual_applied";
                this.mixPanelService.trackVoucherEvent(event, context.selectedDiscountVoucherData);
              } else {
                // Track voucher skipped if applicable
              }

              if (context.selectedDiscountVoucherData) {
                this.voucherService.updateDiscountVoucherRedeemedStatus(
                  context.selectedDiscountVoucherData,
                  true
                );
              }

              this.responseObject = { ...purchaseDetail, ...processPayResult?.response, p_type: "10" } as Data;

              // Update Firestore payment document
              const paymentDocId = String(uuidv4());
              this.updateFirestorePaymentDoc(customerPurchaseId, this.responseObject, context, paymentDocId);

              // Update payment reference
              this.paymentService.updatePaymentReference(customerPurchaseId, paymentDocId).subscribe({
                next: (res) => {
                  this.displayResponse = res;
                },
                error: (err) => {
                  this.authService.errorHandlerLogs("updatePaymentReference failed", err);
                },
              });

              if (processPayResult.response?.syndicate_id) {
                this.paymentService.updateSyndicateRequest(
                  this.responseObject,
                  processPayResult.response?.syndicate_id
                );
              }

              const paymentResult: PaymentResult = {
                success: true,
                customerPurchaseId,
                encryptedCustomerPurchaseId: processPayResult?.response?.encrypted_customer_purchase_id,
                response: processPayResult?.response,
              };

              this.paymentService.emitSyndicatePurchaseComplete();
              callbacks?.onSuccess?.(paymentResult);
              observer.next(paymentResult);
            } else {
              this.checkoutVoucherService.resetAll();
              this.handlePaymentRejection("1050", callbacks);
              observer.error({ error: "Failed in processing payment", errorCode: "1050" });
            }
          },
          error: (err) => {
            this.checkoutVoucherService.resetAll();
            this.handlePaymentRejection("1050", callbacks);
            observer.error(err);
          },
        });
    });
  }

  /**
   * Handle payment rejection with error messages
   */
  private handlePaymentRejection(
    errorCode: string,
    callbacks?: PaymentCallbacks,
    customMessage?: string
  ): void {
    callbacks?.onRejection?.(errorCode);

    // Default error messages based on error code
    const defaultMessages: { [key: string]: string } = {
      "1050": "Failed in processing payment",
      "1051": "Failed in make payment request",
      "1103": "Something went wrong. Please contact support.",
    };

    const message = customMessage || defaultMessages[errorCode] || "Something went wrong!";

    this.authService
      .getConstantsMsg(errorCode)
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: message,
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((error) => {
        this.authService.errorHandlerLogs("Error fetching constants message", error);
      });
  }

  /**
   * Update Firestore payment document
   */
  private updateFirestorePaymentDoc(
    customerPurchaseId: string,
    responseObject: Data,
    context: PaymentContext,
    paymentDocId?: string
  ): void {
    const docId = paymentDocId || String(uuidv4());
    const path_id = "At4Vyy58QQ2vknYAhw7T";
    const doc_data = {
      payment_response: responseObject,
      customer_purchase_id: customerPurchaseId,
    };
    const dv = { docId };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    }).subscribe({
      error: (err) => {
        this.authService.errorHandlerLogs("Error in updatefirestoredoc cloud function", err);
      },
    });
  }

  /**
   * Prepare purchase detail with tracking parameters
   */
  private preparePurchaseDetail(context: PaymentContext): Data {
    const purchaseDetail = { ...context.purchaseDetail };

    purchaseDetail.prosub_id =
      context.prosub_id || localStorage.getItem("prosub_id") || "";
    purchaseDetail.tp1 = context.tp1 || localStorage.getItem("TP1") || "";
    purchaseDetail.tp2 = context.tp2 || localStorage.getItem("TP2") || "";
    purchaseDetail.tp3 = context.tp3 || localStorage.getItem("TP3") || "";
    purchaseDetail.t_id = context.t_id || localStorage.getItem("T_ID") || "";
    purchaseDetail.upsell_id = context.upsell_ID || localStorage.getItem("upsell_ID") || "";
    purchaseDetail.paymentOfferID =
      context.paymentOfferID || localStorage.getItem("paymentOfferID") || "";
    purchaseDetail.other = context.other || localStorage.getItem("Other") || "";

    return purchaseDetail;
  }

  /**
   * Prepare card offer purchase detail
   */
  private prepareCardOfferPurchaseDetail(context: PaymentContext, cardOfferDetail: Data): Data {
    const cardOfferPurchaseDetail = { ...cardOfferDetail };

    cardOfferPurchaseDetail.prosub_id =
      context.prosub_id || localStorage.getItem("prosub_id") || "";
    cardOfferPurchaseDetail.tp1 = "OrderConfirmationBanner";
    cardOfferPurchaseDetail.tp2 = "ProductPromo";
    cardOfferPurchaseDetail.tp3 = context.tp3 || localStorage.getItem("TP3") || "";
    cardOfferPurchaseDetail.t_id = context.t_id || localStorage.getItem("T_ID") || "";
    cardOfferPurchaseDetail.upsell_id = context.upsell_ID || localStorage.getItem("upsell_ID") || "";
    cardOfferPurchaseDetail.paymentOfferID =
      context.paymentOfferID || localStorage.getItem("paymentOfferID") || "";
    cardOfferPurchaseDetail.other = context.other || localStorage.getItem("Other") || "";
    cardOfferPurchaseDetail.checkout = true;
    cardOfferPurchaseDetail.selectedLines = cardOfferDetail.lines;
    cardOfferPurchaseDetail.prize = cardOfferDetail.int_price?.[context.preferredCurrency];

    const offerId: string = cardOfferDetail.offerid;
    cardOfferPurchaseDetail.offers = {};
    cardOfferPurchaseDetail.offers.offerid = cardOfferDetail.offerid;
    cardOfferPurchaseDetail.offers.id = offerId;
    cardOfferPurchaseDetail.offers.price = parseFloat(cardOfferDetail.price);

    return cardOfferPurchaseDetail;
  }

  /**
   * Process add new card flow
   */
  private async processAddNewCardFlow(context: PaymentContext, callbacks?: PaymentCallbacks): Promise<void> {
    try {
      const result: Data = await firstValueFrom(
        this.paymentService.insertCustomerPurchase(
          context.purchaseDetail,
          "/api_post_request/insert_customer_purchase_store_add_card"
        )
      );
      const customerPurchaseId = result?.response?.customer_purchase_id;

      if (result?.status === "rejected") {
        this.addNewCardInProgress = false;
        const translatedMsg = await this.authService.getConstantsMsg(result?.error_code);
        this.authService.showToastMessage(translatedMsg, {
          message: { line_1: "Something went wrong!", line_2: "" },
          style: { icon: "tick circle error.svg", type: "Error" },
        });
        return;
      }

      const success = result?.status === "SUCCESS" || result?.response?.status === "SUCCESS";
      if (!success) return;

      if (context.purchaseDetail?.isCreatingPS && result?.status === "SUCCESS") {
        try {
          const purchase = {
            message: result?.message,
            customer_purchase_id: result?.payload?.order_id,
            status: result?.status,
          };

          this.authService
            .getV2CloudFunction("updatefirestoredoc", {
              path_id: "rDShdjSvpksqa3To0u0R",
              doc_data: { initial_payment: true },
              dv: { docId: context.purchaseDetail.psId },
              method: "update",
            })
            .subscribe({
              error: (err) => {
                this.authService.errorHandlerLogs("Error in updatefirestoredoc cloud function", err);
              },
            });

          this.syndicateService.saveSyndicatPurchaseDetails(context.purchaseDetail.psId, context.purchaseDetail);
          const data = { ...context.purchaseDetail, ...{ customer_purchase: purchase } };
          const myuuid = uuidv4();
          this.paymentService.updateSyndicateRequest(data, myuuid);
          this.paymentService.preferredCurrencyObj = {
            amount: String(context.purchaseDetail?.offers?.int_price?.[context.preferredCurrencySignal?.()]?.toFixed(2)),
            currency: context.preferredCurrencySignal?.() ?? "GBP",
          };

          const cp_request = {
            user_id: context.userId ?? this.authService.userMSN$.value,
            prosub_id: context.purchaseDetail?.prosub_id ?? "",
            TP1: context.tp1 ? context.tp1 : localStorage.getItem("TP1") ? localStorage.getItem("TP1") : "",
            TP2: context.tp2 ? context.tp2 : localStorage.getItem("TP2") ? localStorage.getItem("TP2") : "",
            TP3: context.tp3 ? context.tp3 : localStorage.getItem("TP3") ? localStorage.getItem("TP3") : "",
            upsell_ID: context.upsell_ID
              ? context.upsell_ID
              : localStorage.getItem("upsell_ID")
                ? localStorage.getItem("upsell_ID")
                : "",
            T_ID: context.t_id ? context.t_id : localStorage.getItem("T_ID") ? localStorage.getItem("T_ID") : "",
            offer_id: context.purchaseDetail?.offers?.id ?? "",
            value: context.purchaseDetail?.offers?.price ?? "",
            preferred_currency: {
              Value: context.purchaseDetail?.offers?.price ?? "",
              currency: context.purchaseDetail?.offers?.currency ? context.purchaseDetail?.offers?.currency : "GBP",
            },
            p_type: context.purchaseDetail?.p_type ?? "10",
            product_description: context.purchaseDetail?.product_description ?? "",
          };

          const checkoutRes: Data = await firstValueFrom(
            this.paymentService.getCheckoutId(
              parseFloat(context.purchaseDetail.prize)?.toFixed(2),
              context.preferredCurrencySignal?.()
                ? context.preferredCurrencySignal()
                : context.purchaseDetail?.offers?.currency
                  ? context.purchaseDetail?.offers?.currency
                  : "GBP",
              cp_request
            )
          );
          this.paymentService.preferredCurrencyObj = null;

          if (!checkoutRes) {
            this.addNewCardInProgress = false;
            const translatedMsg = await this.authService.getConstantsMsg("1088");
            const defaultMsg = {
              message: {
                line_1: "Unable to process payment. Please try logging in again.",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
            return;
          }

          if (checkoutRes.id) {
            const checkoutCustomerPurchaseId = customerPurchaseId || result?.payload?.order_id;
            this.paymentService.setCurrentNdcId(checkoutRes.id);
            this.updateCustomerPurchaseDocWithNdc(checkoutRes?.id, checkoutCustomerPurchaseId);
            this.checkoutVoucherService.resetAll();
            await callbacks?.onDismissModal?.();
            await callbacks?.onNavigate?.(`/store/addnew/${String(myuuid)}`, context.purchaseDetail, context.queryParams);

            if (!myuuid || context.userId == "null" || context.userId == null) {
              return;
            }

            const paymentDocId = String(myuuid);
            this.authService
              .getV2CloudFunction("updatefirestoredoc", {
                path_id: "At4Vyy58QQ2vknYAhw7T",
                doc_data: {
                  checkout_response: { ...checkoutRes, ...{ stared: new Date() } },
                  customer_purchase_id: checkoutCustomerPurchaseId,
                },
                dv: { docId: paymentDocId },
                method: "update",
              })
              .subscribe({
                error: (err) => {
                  this.authService.errorHandlerLogs("Error in adding new card:", err);
                },
              });

            this.paymentService.updatePaymentReference(checkoutCustomerPurchaseId, paymentDocId).subscribe({
              next: (res) => {
                this.displayResponse = res;
              },
              error: (err) => {
                this.authService.errorHandlerLogs("updatePaymentReference failed", err);
              },
            });
          } else {
            const translatedMsg = await this.authService.getConstantsMsg("1014");
            const defaultMsg = {
              message: {
                line_1: "something went wrong",
                line_2: "",
              },
              style: {
                icon: "tick circle error.svg",
                type: "Error",
              },
            };
            this.authService.showToastMessage(translatedMsg, defaultMsg);
          }
        } catch (e) {
          this.addNewCardInProgress = false;
          throw e;
        }
      } else {
        const myuuid = uuidv4();
        const mergedData: Data = {
          ...context.purchaseDetail,
          customer_purchase: result?.response,
          ...context.userData,
        };
        if (!mergedData.selectedDiscount) {
          mergedData.selectedDiscount = {};
        }
        this.paymentService.updateSyndicateRequest(mergedData, myuuid);

        this.paymentService.preferredCurrencyObj = {
          amount: String(
            context.purchaseDetail?.offers?.int_price?.[context.preferredCurrencySignal?.()]?.toFixed(2) ??
            parseFloat(context.purchaseDetail?.prize ?? 0)?.toFixed(2)
          ),
          currency: context.preferredCurrencySignal?.() ?? "GBP",
        };

        const prize = context.purchaseDetail?.offers?.price;
        const totalAmountValue = prize + context.totalAmount || prize;
        const cp_request = {
          user_id: context.userId ?? this.authService.userMSN$.value,
          prosub_id: context.purchaseDetail?.prosub_id ?? "",
          TP1: context.tp1 ?? localStorage.getItem("TP1") ?? "",
          TP2: context.tp2 ?? localStorage.getItem("TP2") ?? "",
          TP3: context.tp3 ?? localStorage.getItem("TP3") ?? "",
          upsell_id: context.upsell_ID ?? localStorage.getItem("upsell_ID") ?? "",
          paymentOfferID: context.paymentOfferID ?? localStorage.getItem("paymentOfferID") ?? "",
          T_ID: context.t_id ?? localStorage.getItem("T_ID") ?? "",
          offer_id: context.purchaseDetail?.offers?.offerid ?? context.purchaseDetail?.offer_id,
          value: totalAmountValue,
          preferred_currency: {
            Value: totalAmountValue,
            currency: context.purchaseDetail?.offers?.currency ?? "GBP",
          },
          p_type: context.purchaseDetail?.p_type ?? "10",
          product_description: context.purchaseDetail?.isTopUp
            ? "Award"
            : context.purchaseDetail?.product_description ?? "",
        };

        try {
          const checkoutRes: Data = await firstValueFrom(
            this.paymentService.getCheckoutId(
              parseFloat(context.purchaseDetail?.prize + context.totalAmount)?.toFixed(2),
              context.preferredCurrencySignal?.() ?? context.purchaseDetail?.offers?.currency ?? "GBP",
              cp_request
            )
          );
          this.paymentService.preferredCurrencyObj = null;

          if (!checkoutRes) {
            this.addNewCardInProgress = false;
            const translatedMsg = await this.authService.getConstantsMsg("1088");
            this.authService.showToastMessage(translatedMsg, {
              message: { line_1: "Unable to process payment. Please try logging in again.", line_2: "" },
              style: { icon: "tick circle error.svg", type: "Error" },
            });
            return;
          }

          if (!checkoutRes.id) {
            const translatedMsg = await this.authService.getConstantsMsg("1014");
            this.authService.showToastMessage(translatedMsg, {
              message: { line_1: "something went wrong", line_2: "" },
              style: { icon: "tick circle error.svg", type: "Error" },
            });
            return;
          }

          this.paymentService.setCurrentNdcId(checkoutRes.id);
          this.updateCustomerPurchaseDocWithNdc(checkoutRes?.id, customerPurchaseId);
          this.checkoutVoucherService.resetAll();
          await callbacks?.onDismissModal?.();
          await callbacks?.onNavigate?.(`/store/addnew/${String(myuuid)}`, context.purchaseDetail, context.queryParams);

          if (!myuuid || context.userId === "null" || !context.userId) {
            return;
          }

          try {
            this.authService
              .getV2CloudFunction("updatefirestoredoc", {
                path_id: "At4Vyy58QQ2vknYAhw7T",
                doc_data: {
                  checkout_response: { ...checkoutRes, stared: new Date() },
                  customer_purchase_id: customerPurchaseId,
                },
                dv: { docId: String(myuuid) },
                method: "update",
              })
              .subscribe({
                error: (err) => {
                  this.authService.errorHandlerLogs("Error in updatefirestoredoc cloud function", err);
                },
              });
            this.paymentService.updatePaymentReference(customerPurchaseId, String(myuuid)).subscribe({
              next: (resp) => {
                this.displayResponse = resp;
              },
              error: (err) => {
                this.authService.errorHandlerLogs("updatePaymentReference failed", err);
              },
            });
          } catch (err) {
            this.authService.errorHandlerLogs("Failed to set payment doc", err);
          }
        } catch (err) {
          this.authService.errorHandlerLogs("Checkout ID fetch failed", err);
          this.addNewCardInProgress = false;
          this.showAddCardErrorToast();
        }
      }
    } catch (err) {
      this.authService.errorHandlerLogs("insertCustomerPurchase failed", err);
      this.addNewCardInProgress = false;
      this.showAddCardErrorToast();
    }
  }

  /**
   * Process Checkout.com flow
   */
  private async processCheckoutComFlow(context: PaymentContext, callbacks?: PaymentCallbacks): Promise<void> {
    const myuuid = uuidv4();
    this.checkoutVoucherService.resetAll();

    const data = { ...context.purchaseDetail, ...context.userData };
    this.paymentService.checkOutComPayload$.next(data);

    const modals = Array.from(document.querySelectorAll("ion-modal"))
      .filter((el): el is HTMLIonModalElement => typeof (el as HTMLIonModalElement).dismiss === "function");
    await Promise.all(
      modals.reverse().map(async (m) => {
        try {
          await m.dismiss();
        } catch (err) {
          this.authService.errorHandlerLogs("Failed to dismiss modal", err);
        }
      })
    );

    await callbacks?.onNavigate?.(`/store/addnew/${String(myuuid)}`, context.purchaseDetail, context.queryParams);
  }

  private updateCustomerPurchaseDocWithNdc(ndcId: string, customerPurchaseId: string): void {
    this.paymentService.updateCustomerPurchaseWithNdc(customerPurchaseId, ndcId).subscribe({
      next: () => {
        // No-op
      },
      error: (err) => {
        this.authService.errorHandlerLogs("updateCustomerPurchaseWithNdc failed", err);
      },
    });
  }

  private showAddCardErrorToast(): void {
    this.authService
      .getConstantsMsg("1086")
      .then((translatedMsg) => {
        const defaultMsg = {
          message: {
            line_1: "something went wrong on select card",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };
        this.authService.showToastMessage(translatedMsg, defaultMsg);
      })
      .catch((err) => {
        this.authService.errorHandlerLogs("Error fetching constants message", err);
      });
  }

  /**
   * Process top-up payment
   */
  private processTopUpPayment(context: PaymentContext, callbacks?: PaymentCallbacks): Observable<PaymentResult> {
    return this.payCreditTopUp(context, callbacks);
  }
}
